import React, { useState } from 'react';
import classnames from 'classnames';

import { Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { Translate, LoadingOverlay, CompetitorInput, GenerationErrorDialog } from '@hydra/common/components';

import { sendEvent } from '@hydra/utils/gtm';
import getCompetitorColor from '@hydra/utils/getCompetitorColor';

import {
  useCompetitors,
  useRefreshReview,
  useI18n
} from '@hydra/common/customHooks';

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(3, 2)
  },
  title: { fontSize: '24px' },
  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3)
  },
  dot: {
    marginRight: theme.spacing(2)
  },
  duplicatesError: {
    marginTop: theme.spacing(2),
    color: theme.palette.error.medium
  }
}));

const ManageCompetitors = ({ url, competitors, className: classNameProp }) => {
  const classes = useStyles();
  const { translate } = useI18n();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  const {
    handleAddCompetitors,
    checkValidity,
    handleChange,
    handleClear,
    clearErrors,
    handleSaveSuccess,
    hasDuplicates,
    state
  } = useCompetitors({ projectUrl: url, competitors });
  const refreshReview = useRefreshReview({ url, handleError: error => setGenerationError(error) });

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = async e => {
    e.preventDefault();
    setLoading(true);
    // FE validation
    if (!checkValidity()) {
      setLoading(false);
      return false;
    }
    const result = await handleAddCompetitors();
    if (!result) {
      setLoading(false);
      return false;
    }
    clearErrors();
    handleSaveSuccess(true);
    refreshReview();
    setLoading(false);
  };

  const handleClick = () => {
    sendEvent('review-actions-managecompetitors-button');
    setOpen(true);
  };

  return (
    <>
      <GenerationErrorDialog
        open={!!generationError}
        onClose={() => setGenerationError(null)}
        error={generationError}
      />
      <Button
        variant='outlined'
        size='small'
        className={classnames(classNameProp, 'nr-review-managecompetitors-button')}
        onClick={handleClick}
      >
        <Translate content='dashboard_manage_competitors' />
      </Button>
      <Dialog open={open} onClose={handleClose} classes={{ paper: classes.paper }} fullWidth maxWidth='sm'>
        <DialogTitle align='center'>
          <Translate content='dashboard_manage_competitors_modal_title' with={{ domain: url }} className={classes.title} />
        </DialogTitle>
        <DialogContent className={classes.content}>
          <LoadingOverlay isLoading={loading} />
          {Object.keys(state).map((key, index) => {
            const {
              name,
              value,
              label,
              valid,
              placeholder,
              errorMessage
            } = state[key];
            return (
              <CompetitorInput
                key={index}
                name={name}
                value={value}
                label={translate(label)}
                placeholder={translate(placeholder)}
                handleChange={handleChange}
                handleClear={handleClear}
                color={getCompetitorColor(index) || ''}
                valid={valid}
                helperText={!valid && errorMessage}
              />
            );
          })}
          {hasDuplicates && (
            <Translate
              className={classes.duplicatesError}
              component='span'
              content='competitors_duplicate_error'
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color='primary'>
            <Translate content='cancel' />
          </Button>
          <Button onClick={handleConfirm} color='primary' variant='contained'>
            <Translate content='confirm' />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageCompetitors;
